/**
 * "yanamas adjusted today's plan" — the SC-13 pull surface
 * ========================================================
 *
 * Constitution SC-13: autonomous changes are **quiet by default, never
 * mysterious**. The backend already records every plan change; this is the
 * only place a human can read it back, and it is the third leg SC-14 makes
 * HOLD-first conditional on (with the Flexible/Confirmed distinction and
 * zero-nag).
 *
 * QUIET BY DEFAULT, structurally — the constraints, and how each is met:
 *   - nothing fires on its own. No toast, no notification, no live region,
 *     no polling, no subscription. Proactive copy about changes belongs to
 *     the Motivator delivery gate, which is a different lane (SC-2).
 *   - nothing demands attention. The affordance is one line of muted text
 *     with the same weight as "Earlier today (2)" — no colour, no dot, no
 *     badge, and it never auto-expands.
 *   - **an empty log renders NOTHING.** Not an empty state, not "no
 *     changes today" — a day Horton did not touch must look exactly like a
 *     day with no such surface at all. Until the Phase 3 reconciler exists
 *     that is the common case, and an empty-state here would invent the
 *     impression that something is missing.
 *   - an unreachable endpoint also renders nothing. We cannot claim a plan
 *     changed, and SC-13 promises the record is *available*, never that
 *     the user is told it is there.
 *
 * FACTS, NOT PROSE (SC-3): every sentence comes from a fixed table in
 * planChangePresentation.ts, keyed by the canonical symbol the engine
 * recorded. This component composes no explanations of its own, and an
 * unrecognised symbol renders nothing rather than leaking an internal word
 * (SC-36) or being guessed at.
 *
 * Read-only by construction: there is no verb on this surface, so there is
 * no 202 `applied:false` contract to honour here — the mutations that
 * produce these rows live in the scheduler lanes, and their clients handle
 * accepted-not-applied themselves.
 */

import { useEffect, useState } from 'react'
import { ChevronDown, History } from 'lucide-react'
import { fetchPlanChanges } from '../../services/scheduleService'
import type { PlanChange } from '../../services/scheduleService'
import {
  displacedCopy,
  itemLine,
  placementMoved,
  PLAN_CHANGE_COPY,
  REASON_COPY,
  TRIGGER_COPY,
} from './planChangePresentation'

export interface PlanAdjustmentsProps {
  /** The day to explain, "YYYY-MM-DD" — the user's local day. The server
   *  refuses to guess which day is today (SC-35); the browser knows. */
  localDate: string
  /** Bumped by the page when it refetches its day, so the history follows
   *  a rebalance that happened while this screen was open. Not a poll. */
  refreshKey?: number
}

function ChangeRow({ change, surfaceDate }: { change: PlanChange; surfaceDate: string }) {
  const reason = change.reasonCode ? REASON_COPY[change.reasonCode] : undefined
  const trigger = change.triggerEvent ? TRIGGER_COPY[change.triggerEvent] : undefined
  const displaced = displacedCopy(change.displacedTo)
  const moved = placementMoved(change.previous, change.replacement)
  const previousLine = itemLine(change.previous, surfaceDate)
  const replacementLine = itemLine(change.replacement, surfaceDate)
  // A change that moved nothing (a demotion re-records the same block at the
  // same coordinates) is ONE item whose commitment changed, not a before and
  // an after — the reason line is what changed about it.
  const singleLine = moved ? null : replacementLine ?? previousLine

  return (
    <li className="space-y-0.5">
      {reason && <p className="text-app-micro text-secondary leading-relaxed">{reason}</p>}
      {singleLine && <p className="text-app-micro text-muted">{singleLine}</p>}
      {moved && previousLine && (
        <p className="text-app-micro text-muted">
          <span className="text-secondary">{PLAN_CHANGE_COPY.was}</span> {previousLine}
        </p>
      )}
      {moved && replacementLine && (
        <p className="text-app-micro text-muted">
          <span className="text-secondary">{PLAN_CHANGE_COPY.now}</span> {replacementLine}
        </p>
      )}
      {displaced && <p className="text-app-micro text-muted">{displaced}</p>}
      {trigger && <p className="text-app-micro text-muted italic">{trigger}</p>}
    </li>
  )
}

export function PlanAdjustments({ localDate, refreshKey }: PlanAdjustmentsProps) {
  const [changes, setChanges] = useState<PlanChange[]>([])
  const [open, setOpen] = useState(false)

  // A new day is a different question. Clearing first means yesterday's
  // changes can never sit under today's label while the fetch is in flight;
  // a plain refresh does not clear, so the affordance does not blink.
  useEffect(() => {
    // Both setters bail out when nothing actually changes, so a mount does
    // not cost a second render.
    setChanges(prev => (prev.length === 0 ? prev : []))
    setOpen(false)
  }, [localDate])

  useEffect(() => {
    let cancelled = false
    // One read, on demand, when the day is on screen. `fetchPlanChanges`
    // never throws — an unreachable endpoint answers the same as a quiet
    // day, and both render nothing.
    void fetchPlanChanges(localDate).then(rows => {
      if (!cancelled) setChanges(rows)
    })
    return () => { cancelled = true }
  }, [localDate, refreshKey])

  // The whole surface, gone. Not an empty state — nothing.
  if (changes.length === 0) return null

  return (
    <div className="pt-1">
      <button
        type="button"
        onClick={() => setOpen(v => !v)}
        aria-expanded={open}
        className="min-h-touch text-app-micro text-muted hover:text-secondary inline-flex items-center gap-1.5 text-left"
      >
        <History className="w-3 h-3 shrink-0" aria-hidden="true" />
        {PLAN_CHANGE_COPY.summary(changes.length)}
        <ChevronDown
          className={`w-3 h-3 shrink-0 transition-transform${open ? ' rotate-180' : ''}`}
          aria-hidden="true"
        />
      </button>
      {open && (
        <ul className="mt-2 space-y-2.5 rounded-lg bg-surface-alt px-2.5 py-2">
          {changes.map(change => (
            <ChangeRow key={change.key} change={change} surfaceDate={localDate} />
          ))}
        </ul>
      )}
    </div>
  )
}

export default PlanAdjustments
